import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Radio,
  Stack,
} from '@chakra-ui/react'
import { RadioGroupFormControl } from 'BrokerTsLib/src/components'
import type { InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import { Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect } from 'react'

import { ContractReplacement } from '@/components/ContractReplacement'
import { ReactHookFormDatepicker as Datepicker } from '@/containers/forms/Datepicker'
import { FormBody } from '@/containers/forms/FormBody'
import { FormRow } from '@/containers/forms/FormRow'
import { RadioGroup } from '@/containers/forms/RadioGroup'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { Apps } from '@/dtos'
import { useSelections } from '@/hooks'
import { useApp } from '@/hooks/useApp'
import { useNameof } from '@/hooks/useNameof'
import { formatDate } from '@/utils/formatDate'

const suggestDates = (args?: { count: number }) => {
  const { count } = args ?? { count: 5 }
  const today = new Date()

  return Array.from({ length: count }).map((_, index) => {
    const date = new Date(today)
    date.setDate(date.getDate() + index + 1)
    return date
  })
}

export const InformationForm = () => {
  const { app } = useApp()
  const nameof = useNameof<InsuranceParamsDto>()

  const methods = useFormContext<InsuranceParamsDto>()
  const {
    register,
    setValue,
    getValues,
    formState: { errors },
    watch
  } = methods

  const { isDisabled } = useInsuranceParams()
  const suggestedDates = suggestDates()

  const handleSuggestDateClick = (date: Date) => {
    if (date)
      setValue('generalParams.insuranceStartDate', date)
  }

  const { data: dataPf } = useSelections(SelectionType.FrekvencePlatby)

  const paymentType = watch('generalParams.paymentType')
  const { data: dataPt } = useSelections(SelectionType.ZpusobPlatby)
  useEffect(() => {
    if (dataPt?.data.length === 1)
      setValue('generalParams.paymentType', dataPt.data[0].id)
  }, [dataPt])

  return (
    <>
      <FormBody>
        <FormRow
          sx={{
            gap: {
              base: 0,
              lg: 8
            }
          }}
        >
          <FormControl isInvalid={!!errors.generalParams?.insuranceStartDate}>
            <FormLabel>Datum počátku pojištění</FormLabel>

            <Datepicker
              isDisabled={isDisabled}
              name='generalParams.insuranceStartDate'
            />
          </FormControl>

          <FormControl>
            <FormLabel
              sx={{
                display: {
                  base: 'none',
                  lg: 'block',
                },
              }}
            >
              &nbsp;
            </FormLabel>

            {!isDisabled &&
              <Stack direction="row" spacing={2}>
                {suggestedDates.map((date) => (
                  <Button
                    key={date.toISOString()}
                    variant="ghost"
                    size="sm"
                    sx={{ h: 10 }}
                    onClick={() => handleSuggestDateClick(date)}
                  >
                    {formatDate(date, 'dd.MM.')}
                  </Button>
                ))}
              </Stack>
            }
          </FormControl>
        </FormRow>

        {app != Apps.extra &&
          <>
            {getValues('generalParams.contractReplacementNumber') &&
              <FormRow itemsCount={1}>
                <ContractReplacement isDisabled defaultReplacement={!!getValues('generalParams.contractReplacementNumber')} />
              </FormRow>
            }

            <FormRow>
              <FormControl>
                <FormLabel>Frekvence platby</FormLabel>

                <FormHelperText>
                  Frekvence platby menší než 1x za rok může zvýšit celkové
                  pojistné
                </FormHelperText>

                <RadioGroup
                  name={nameof(x => x.generalParams.paymentFrequency)}
                  isDisabled={isDisabled}
                  stackProps={{
                    direction: 'column',
                    spacing: 4,
                  }}
                >
                  {dataPf?.data.filter((item) => item.id != Selection.OneTime).map((item) => (
                    <Radio value={item.id.toString()} key={item.id.toString()}>
                      {item.description}
                    </Radio>
                  ))}
                </RadioGroup>
              </FormControl>

              {((dataPt?.data.length ?? 0) > 1 || !paymentType) &&
                <RadioGroupFormControl name={nameof(x => x.generalParams.paymentType)}
                  valueAsNumber
                  isDisabled={isDisabled}
                  label="Způsob platby"
                  helperText="&nbsp;"
                  helperTextPosition='top'
                >
                  <Stack gap={3}>
                    {dataPt?.data.map((item) => (
                      <Radio value={item.id.toString()} key={item.id.toString()}>
                        {item.description}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroupFormControl>
              }
            </FormRow>
          </>
        }

        <FormControl sx={{ w: '100%' }}>
          <FormLabel>Název nabídky</FormLabel>

          <Input
            placeholder="Napište název nabídky"
            isDisabled={isDisabled}
            {...register('generalParams.name')}
          />
        </FormControl>
      </FormBody>
    </>
  )
}